import React from "react";
import { PenetrationTestingCard, PenetrationTestingSection } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import Image from "next/image";
import { renderRichText } from "@/lib/richText";

interface PenetrationTestingSectionProps {
  blok: PenetrationTestingSection & SbBlokData;
}

const PenetrationTestingSectionComponent: React.FC<PenetrationTestingSectionProps> = ({ blok }) => {
  if (!blok) return null;

  const { title, description, cards, bg_style } = blok;
  const getBgClass = () => bg_style === "dark" ? "bg-light" : "";

  if (blok.type === "red_icon") {
    return (
      <div className={getBgClass()}>
        <div className="container d-flex flex-column justify-content-center py-4 py-md-5" {...storyblokEditable(blok)}>
          {title && (
            <h2 className="text-center mb-4" id={title.toLowerCase().replace(/\s+/g, "-")}>
              {title}
            </h2>
          )}
          {description && (
            <div
              className="lead text-center mb-4"
              dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
            />
          )}
          <div className="row row-cols-1 justify-content-md-center gy-4">
            {cards?.map((card: PenetrationTestingCard) => {
              const typedCard = card as unknown as SbBlokData;
              return (
                <div key={card._uid} className="col-lg-20 col-xl-18 col-xxl-16" {...storyblokEditable(typedCard)}>
                  <div className="d-md-flex align-items-md-center text-center text-md-start">
                    {card.icon?.filename && (
                      <Image
                        src={card.icon.filename}
                        width={90}
                        height={90}
                        alt={card.title || ""}
                        title={card.title || ""}
                        className="mb-3 mb-md-0"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        loading="lazy"
                        quality={75}
                      />
                    )}
                    <div className="ms-md-4">
                      {card.title && <h3 className="mb-2">{card.title}</h3>}
                      {card.description && (
                        <div
                          className="mb-o"
                          dangerouslySetInnerHTML={{ __html: renderRichText(card.description) }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (blok.type === "blog_number_blue") {
    return (
      <div className={getBgClass()} {...storyblokEditable(blok)}>
        <div className="container">
          <div className="py-4 py-lg-5">
            <div className="two-thirds-centered">
              {title && (
                <h2 className="mb-4 text-secondary" id={title.toLowerCase().replace(/\s+/g, "-")}>
                  {title}
                </h2>
              )}
              {description && (
                <div className="lead mb-4" dangerouslySetInnerHTML={{ __html: renderRichText(description) }} />
              )}
              {cards && cards.length > 0 && (
                <ol start={1} className="list-unstyled list-numbered-styled list-numbered-styled-secondary">
                  {cards.map((card, index) => (
                    <li key={card._uid}
                        itemProp="itemListElement"
                        itemScope
                        itemType="http://schema.org/ListItem"
                    >
                      <span className="list-number" itemProp="position">{index + 1}</span>
                      <div itemProp="name">
                        {card.title && <h3 className="text-secondary">{card.title}</h3>}
                        {card.description && (
                          <div dangerouslySetInnerHTML={{ __html: renderRichText(card.description) }} />
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (blok.type === "blog_number_red") {
    return (
      <div className={getBgClass()} {...storyblokEditable(blok)}>
        <div className="container">
          <div className="py-4 py-lg-5">
            <div className="two-thirds-centered">
              {title && (
                <h2 className="mb-4 text-primary" id={title.toLowerCase().replace(/\s+/g, "-")}>
                  {title}
                </h2>
              )}
              {description && (
                <div className="lead mb-4" dangerouslySetInnerHTML={{ __html: renderRichText(description) }} />
              )}
              {cards && cards.length > 0 && (
                <ol start={1} className="list-unstyled list-numbered-styled list-numbered-styled-primary">
                  {cards.map((card, index) => (
                    <li key={card._uid}
                        itemProp="itemListElement"
                        itemScope
                        itemType="http://schema.org/ListItem"
                    >
                      <span className="list-number" itemProp="position">{index + 1}</span>
                      <div itemProp="name">
                        {card.title && <h3 className="text-primary">{card.title}</h3>}
                        {card.description && (
                          <div dangerouslySetInnerHTML={{ __html: renderRichText(card.description) }} />
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (blok.type === "blog_red_shield") {
    return (
      <div className={getBgClass()}>
        <div className="container" {...storyblokEditable(blok)}>
          <div className="py-4 py-lg-5">
            <div className="two-thirds-centered">
              {title && (
                <h2 className="mb-4" id={title.toLowerCase().replace(/\s+/g, "-")}>
                  {title}
                </h2>
              )}
              {description && (
                <div className="lead mb-4" dangerouslySetInnerHTML={{ __html: renderRichText(description) }} />
              )}
              {cards && cards.length > 0 && (
                <ol start={1} className="list-unstyled list-numbered-shield-styled">
                  {cards.map((card) => (
                    <li key={card._uid}>
                      {card.title && <h3>{card.title}</h3>}
                      {card.description && (
                        <div dangerouslySetInnerHTML={{ __html: renderRichText(card.description) }} />
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (blok.type === "blog_red_icon") {
    return (
      <div className={getBgClass()}>
        <div className="container" {...storyblokEditable(blok)}>
          <div className="two-thirds-centered">
            <section className="py-3 py-lg-4">
              {title && (
                <h2 id={title.toLowerCase().replace(/\s+/g, "-")}>{title}</h2>
              )}
              {description && (
                <div dangerouslySetInnerHTML={{ __html: renderRichText(description) }} />
              )}
              {cards && cards.length > 0 && cards.map((card) => (
                <div key={card._uid} className="d-lg-flex my-4 text-start">
                  <div className="me-4">
                    {card.icon?.filename && (
                      <Image
                        src={card.icon.filename}
                        width={120}
                        height={120}
                        alt={card.title || ""}
                        title={card.title || ""}
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        loading="lazy"
                        quality={75}
                      />
                    )}
                  </div>
                  <div>
                    {card.title && <h3>{card.title}</h3>}
                    {card.description && (
                      <div className="mb-0"
                         dangerouslySetInnerHTML={{ __html: renderRichText(card.description) }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </section>
          </div>
        </div>
      </div>
    );
  }

  if (blok.type === "blog_dark_bg") {
    return (
      <div className="bg-dark component-simple-content py-4 py-md-5" {...storyblokEditable(blok)}>
        <div className="container max-two-thirds lead">
          {title && (
            <h2 className="col-24 mb-4" id={title.toLowerCase().replace(/\s+/g, "-")}>
              {title}
            </h2>
          )}
          {description && (
            <div
              className="mb-4"
              dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
            />
          )}
          {cards && cards.length > 0 && (
            <ol start={1} className="list-unstyled list-numbered-styled mb-0">
              {cards.map((cardItem, index) => {
                const card = cardItem;
                return (
                  <li
                    key={card._uid}
                    itemProp="itemListElement"
                    itemScope
                    itemType="http://schema.org/ListItem"
                  >
                    <span className="list-number" itemProp="position">{index + 1}</span>
                    <span itemProp="name">
                      {card.title && <h3>{card.title}</h3>}
                      {card.description && (
                        <div
                          className="mb-0"
                          dangerouslySetInnerHTML={{ __html: renderRichText(card.description) }}
                        />
                      )}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    );
  }

  if (blok.type === "blog_red_bg") {
    return (
      <div className="bg-primary component-simple-content py-4 py-md-5" {...storyblokEditable(blok)}>
        <div className="container max-two-thirds lead text-white">
          {title && (
            <h2 className="col-24 mb-4 text-white" id={title.toLowerCase().replace(/\s+/g, "-")}>
              {title}
            </h2>
          )}
          {cards && cards.length > 0 && (
            <ol start={1} className="list-unstyled list-numbered-styled mb-0">
              {cards.map((card, index) => (
                <li
                  key={card._uid}
                  itemProp="itemListElement"
                  itemScope
                  itemType="http://schema.org/ListItem"
                >
                  <span className="list-number" itemProp="position">{index + 1}</span>
                  <span itemProp="name">
                  {card.title && <h3>{card.title}</h3>}
                    {card.description && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: renderRichText(card.description)
                            .replace(/<a /g, '<a class="text-white" target="_blank" ')
                        }}
                      />
                    )}
                </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    );
  }

  if (blok.type === "blog_red_icon_class") {
    return (
      <div className="bg-white component-simple-content py-4 py-md-5" {...storyblokEditable(blok)}>
        <div className="container max-two-thirds lead">
          {title && (
            <h2 className="col-24 mb-4" id={title.toLowerCase().replace(/\s+/g, "-")}>
              {title}
            </h2>
          )}
          {description && (
            <div className="lead mb-5" dangerouslySetInnerHTML={{ __html: renderRichText(description) }} />
          )}
          {cards && cards.map((card) => (
            <div key={card._uid} className="row mb-2">
              <div className="col-24 col-lg-4 col-xl-5">
                <i
                  className={`fas ${card.icon_class} me-2 fa-4x text-primary icon-circled`}
                  aria-hidden="true"
                ></i>
              </div>
              <div className="col-24 col-lg-20 col-xl-19">
                {card.title && <h3>{card.title}</h3>}
                {card.description && (
                  <div
                    className="lead"
                    dangerouslySetInnerHTML={{ __html: renderRichText(card.description) }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (blok.type === "blog_blue_icon") {
    return (
      <div className={getBgClass()}>
        <div className="container" {...storyblokEditable(blok)}>
          <div className="two-thirds-centered">
            <section className="py-3 py-lg-4">
              {title && (
                <h2 className="text-primary" id={title.toLowerCase().replace(/\s+/g, "-")}>
                  {title}
                </h2>
              )}
              {description && (
                <div dangerouslySetInnerHTML={{ __html: renderRichText(description) }} />
              )}
              {cards && cards.length > 0 && cards.map((card) => (
                <div key={card._uid} className="d-lg-flex my-4 text-start">
                  <div className="me-4">
                    {card.icon?.filename && (
                      <Image
                        src={card.icon.filename}
                        width={40}
                        height={40}
                        alt={card.title || ""}
                        title={card.title || ""}
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        loading="lazy"
                        quality={75}
                      />
                    )}
                  </div>
                  <div>
                    {card.title && <h3 className="text-secondary">{card.title}</h3>}
                    {card.description && (
                      <div className="mb-0"
                           dangerouslySetInnerHTML={{ __html: renderRichText(card.description) }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </section>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className={getBgClass()}>

      <div {...(blok && storyblokEditable(blok))}>
        <div className="container py-4 py-md-5 gx-0">
          {title && (
            <h2 className="text-center mb-3" id={title.toLowerCase().replace(/\s+/g, "-")}>
              {title}
            </h2>
          )}

          {description && (
            <div
              className="lead text-center mb-4 mb-lg-5 col-xl-16 mx-auto"
              dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
            />
          )}

          {cards && cards.length > 0 && (
            <div className="methodology-list px-3 px-lg-0">
              <ol className="list-unstyled">
                {cards.map((card, index) => (
                  <li key={card._uid} className="scroll-trigger">
                    <div className="card card-lighter p-3 p-lg-4 d-flex flex-row flex-nowrap align-items-center">
                      {card.icon && card.icon.filename && (
                        <Image
                          src={card.icon.filename}
                          width={130}
                          height={130}
                          alt={card.title || `Step ${index + 1}`}
                          className="me-3"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          loading="lazy"
                          quality={75}
                        />
                      )}
                      <div>
                        <h3 className="h4 mb-1 text-secondary">{card.title}</h3>
                        {card.description && (
                          <div
                            className="mb-o"
                            dangerouslySetInnerHTML={{ __html: renderRichText(card.description) }}
                          />
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default PenetrationTestingSectionComponent;